<template>
    <div>
        <table-page
            v-show="!addingBulkPositions"
            :client="client"
            @updated="getPositions"
            :warnings="warnings"
            :changelogs="changelogs"
            :hideTabs="true"
        >
            <fast-table
                :recordLabel="recordLabel"
                :sections="sections"
                :headers="headers"
                :data="tableData"
                :activeRowId="currentPositionId"
                @click="currentPositionId = $event.id"
            >
                <template v-slot:table-actions>
                    <button class="btn btn-outline-primary btn-collapse mr-1" id="addBulkPositionsButton" @click="addBulkPositions">
                        <i class="far fa-fw fa-tools"></i> <span class="btn-text">Bulk Setup</span>
                    </button>
                    <button class="btn btn-primary btn-collapse" id="addPositionButton" @click="addPosition">
                        <i class="far fa-fw fa-plus"></i> <span class="btn-text">Add Position</span>
                    </button>
                </template>
            </fast-table>

            <add-position-modal v-if="addPositionModalOpen" :client="client" ref="addPositionModal" @updated="$emit('updated'); getPositions()" @close="addPositionModalOpen = false"/>
        </table-page>

        <div v-if="addingBulkPositions" class="flex-body body-scroll">
            <div class="container-fluid">
                <div class="container-centered-m py-4">
                    <h3>Positions</h3>

                    <p>Download the template for positions and then upload the revised file.</p>

                    <div>
                        <div>
                            <a class="btn btn-link text-center ml-auto mr-auto mb-2 w-100" :href="downloadTemplateUrl()"><i class="far fa-download mr-hf"></i>Download Position Template</a>

                            <!-- <input class="d-none" type="file" @change="onUpload($event)" accept=".xls, .xlsx" ref="fileInput"/>
                            <button class="btn btn-outline-primary btn-boxy" @click.prevent="$refs.fileInput.click()">
                                <i class="far fa-upload mr-hf"></i>
                                <span>Add Positions</span>
                                <p class="text-dark text-small">Supported file types: .xls, .xlsx</p>
                            </button> -->
                            <form-input type="file" v-model="formData.file" :errors="errors.file" label="Upload Positions Spreadsheet:" helper-text="File extension must be .xlsx" accept=".xlsx" @upload="onUpload($event)" />
                        </div>

                        <form>
                            <form-errors :errors="formErrors"/>

                            <div class="row mb-2 mt-2 d-flex align-items-center">
                                <div class="col">
                                    <button @click="addingBulkPositions = false" class="btn-outline-primary btn">Back</button>
                                </div>

                                <div class="col text-right">
                                    <button class="btn btn-primary" @click.prevent="onSubmit">Save</button>
                                </div>
                            </div>
                        </form>
                    </div>

                </div>
            </div>
        </div>

        <position-sidebar
            v-if="currentPosition"
            :client="client"
            :position-id="currentPosition.id"
            ref="positionSidebar"
            @updated="getPositions"
            @close="currentPositionId = null"
        >
        </position-sidebar>
    </div>
</template>

<script>
import TablePageMixin from '@/mixins/TablePageMixin'
import FormMixin from '@/mixins/Form'
import FastTable from '@/components/FastTable'
import PositionSidebar from './components/Positions/PositionSidebar'
import AddPositionModal from './components/Positions/AddPositionModal'

export default {
    components: {FastTable, PositionSidebar, AddPositionModal},
    mixins: [TablePageMixin, FormMixin],
    props: ['client'],
    computed: {
        currentPosition() {
            return this.positions.find(e => (e.id == this.currentPositionId))
        },
        sections() {
            let sections = [
                {title: 'Active', defaultIsClosed: false, id: 'active', hasDateFilter: false},
                {title: 'Inactive', defaultIsClosed: true, id: 'inactive', hasDateFilter: true},
            ]
            return sections
        },
        activePositions() {
            return this.positions.filter((p) => {return p.is_active})
        },
        inactivePositions() {
            return this.positions.filter((p) => {return !p.is_active})
        },
        tableData() {
            const tableData = []
            const subsets = [
                this.activePositions,
                this.inactivePositions,
            ]

            subsets.forEach(positions => {
                const sectionData = []

                positions.forEach(position => {
                    sectionData.push({
                        id: position.id,
                        object: position,
                        cells: this.makePositionTableData(position),
                    })
                })
                tableData.push(sectionData)
            })
            return tableData
        },
    },
    data() {
        let data = {
            positions: [],
            dataLoaded: false,
            currentPositionId: null,
            recordLabel: "Positions",
            headers: [
                {label: 'Company', classes: 'cell-company', isSearchable: true, defaultSort: true, isFilterable: true},
            ],
            addingBulkPositions: false,
            addPositionModalOpen: false,
            formData: this.makeFormData()
        }
        let orgLevelHeaders = this.client.org_level_labels
        if (this.client.aos_client) {
            let locationOrgLevelIdx = this.client.location_org_level ? this.client.location_org_level - 1 : 0
            orgLevelHeaders.splice(locationOrgLevelIdx, 0, 'Location')
        }
        orgLevelHeaders.forEach(label => {
            data.headers.push({label: label, classes: 'cw-12', isSearchable: true, isFilterable: true})
        })
        data.headers.push(
            {label: 'Warnings', classes: 'cs-warning', isSearchable: false, defaultSort: true},
            {label: 'Company #', classes: 'cell-right', isSearchable: false, defaultSort: true},
            {label: 'Employees', classes: 'cell-right', isSearchable: false, defaultSort: false},
        )
        return data
    },
    mounted() {
        this.$store.dispatch('SET_PAGE_TITLE', 'Settings / Positions')
        this.getPositions()
    },
    methods: {
        getPositions() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/positions`).then(resp => {
                this.positions = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                if (errors.__status__ == 403) {
                    this.$bus.$emit('no-access')
                }
                else {
                    this.$bus.showError(errors.__all__)
                }
            })
        },
        makePositionTableData(position) {
            let data = [
                position.company.name,
            ]
            let locationOrgLevelIdx = this.client.location_org_level ? this.client.location_org_level - 1 : 0
            let orgLevelValues = Array.from(position.terminal_value_tuple).slice(0, this.client.org_level_labels.length)
            if (this.client.aos_client) {
                orgLevelValues.splice(locationOrgLevelIdx, 0, position.work_location.name)
            } else {
                orgLevelValues[locationOrgLevelIdx] = position.work_location.name
            }
            data.push(...orgLevelValues)
            data.push(
                '', // warnings
                position.company.company_code,
                position.num_employees
            )
            return data
        },
        addPosition() {
            this.addPositionModalOpen = true
        },
        addBulkPositions() {
            this.addingBulkPositions = true
        },
        downloadTemplateUrl() {
            return `${this.$api.API_BASE}/clients/${this.client.id}/settings/positions/template`
        },
        onUpload(f) {
            this.formData.file = f[0]
        },
        makeFormData() {
            return {
                file: null,
            }
        },
        getFormData() {
            return this.formData
        },
        onSubmit() {
            this.$store.dispatch('START_LOADING')
            return this.$api.upload(`/clients/${this.client.id}/settings/positions/upload`, this.formData.file, {fieldName: 'file', formData: this.formData}).then((resp) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully uploaded ${resp.length} positions`)
                this.$emit('updated')
                this.addingBulkPositions = false
            }).catch((errors) => {
                console.log(errors)
                this.$store.dispatch('STOP_LOADING')

                this.formErrors = (errors.__all__ || [])
                this.$bus.showError('Please check the form for any missing fields.')
            })
        },
    },
}
</script>
